import { useId, useMemo } from 'react'
import { encodeCode128B, QUIET_ZONE } from '../lib/code128'
import '../styles/logo.css'

/**
 * The Puresoft brand mark — the "Dusk Blue Barcode" logo.
 *
 * THERE IS EXACTLY ONE OFFICIAL LOCKUP and this component renders it: a real
 * Code 128-B barcode with the mountain range cut out of it as NEGATIVE SPACE (the
 * page background shows through the range — never fill it), snow on the three
 * tallest peaks, and the two-tone lowercase "puresoft" wordmark hanging below.
 *
 * The old `stacked` / `horizontal` / `square` variants were retired (2026-08-29).
 * Do not reintroduce a variant prop. If a surface cannot fit this lockup, that is a
 * layout problem to solve there, not a reason to fork the mark — every previous fork
 * (the report renderer, the favicon, offline.html) has drifted from the original.
 *
 * Colours come from CSS custom properties (see styles/logo.css) so the mark inverts
 * with the app's light/dark toggle. There is no white backing plate — the mark is
 * bars + shading only, and that rule is what makes dark mode an inverted (i.e. not
 * scannable) barcode. That trade is deliberate; see logo.css.
 *
 * All geometry derives from `height`, so the lockup scales as one unit. The mask
 * needs a document-unique id (several logos can share a page), so we take one from
 * React's useId().
 */

// The mountain range and its snow, authored in a 167-wide box with the range
// spanning y=6 (peak) to y=42 (below the bar band, so the polygon closes along the
// bottom edge). fitRange() re-fits these onto whatever width the symbol comes out.
const BANNER_SNOW: readonly string[] = [
  '52,6 52,29 59,22 64,26 66,22 60,18',
  '80,14 83,27 89,23 94,28 90,20',
  '106,16 109,29 115,24 120,26 114,20',
]

const BANNER_MOUNTAIN = '16,42 34,18 44,26 52,6 66,22 80,14 94,28 106,16 120,26 128,22 146,42'

/** What the mark spells out. Code Set B, so any ASCII 32..126 works. */
export const CODE128_PAYLOAD = 'puresoft'

/** Height of the bar band, in module units. Bars run 0..this. */
const CODE128_HEIGHT = 40

/**
 * The highest the mountain range may reach, in module units from the top of the
 * bars. Everything above this line is an uninterrupted full-width run of correctly
 * proportioned bars, and a scanner only needs one clean horizontal line across the
 * symbol — so this value is the whole reason the mark both looks like the brand
 * and still decodes.
 *
 * 8 of 40 is not a guess. Rendering the mark and running a real decoder over it,
 * anything above 6 fails at small sizes and the old decorative geometry (peak flush
 * with the top of the bars, i.e. 0) never decodes at all. Lowering this number grows
 * the range and shrinks the scan band; do not, without re-measuring.
 */
const CODE128_RANGE_TOP = 8

const RANGE_SRC_WIDTH = 167
const RANGE_SRC_TOP = 6
const RANGE_SRC_BOTTOM = 42

/**
 * How far right every fitted polygon slides, as a fraction of the mark's width.
 *
 * In the source art the range's feet sit at x=16 and x=146 of a 167-wide box, so its
 * midpoint is 81/167 — about 1.5% of the width left of centre. Scaling alone carries
 * that lean over and leaves the whole cutout, and the wordmark nested in it, sitting
 * left of the symbol. This puts the foot span back on the barcode's own centre.
 *
 * Derived from the MOUNTAIN alone and then applied to every polygon: the snow caps
 * have their own, narrower x-extents, so letting each compute its own shift would
 * slide them off the peaks they belong to.
 */
const RANGE_CENTRE_SHIFT = (() => {
  const xs = BANNER_MOUNTAIN.split(' ').map((point) => Number(point.split(',')[0]))
  return 0.5 - (Math.min(...xs) + Math.max(...xs)) / 2 / RANGE_SRC_WIDTH
})()

/**
 * Re-fit a polygon onto a barcode `width` modules wide: x scales with the width and
 * slides by RANGE_CENTRE_SHIFT, y is compressed into
 * CODE128_RANGE_TOP..CODE128_HEIGHT so the peaks stop short of the top instead of
 * slicing the bars clean through.
 */
function fitRange(points: string, width: number): string {
  const scaleY = (CODE128_HEIGHT - CODE128_RANGE_TOP) / (RANGE_SRC_BOTTOM - RANGE_SRC_TOP)
  return points
    .split(' ')
    .map((point) => {
      const [x, y] = point.split(',').map(Number)
      const fx = (x / RANGE_SRC_WIDTH + RANGE_CENTRE_SHIFT) * width
      const fy = CODE128_RANGE_TOP + (y - RANGE_SRC_TOP) * scaleY
      return `${fx.toFixed(2)},${fy.toFixed(2)}`
    })
    .join(' ')
}

/**
 * Ramp stops across the symbol. Five, not the six the old decorative mark used: the
 * palest stop (#a9c7e2) is the one colour that makes the symbol undecodable at every
 * geometry — measured, not guessed — so the ramp starts one stop in.
 */
const CODE128_RAMP_STOPS = 5

// ---- Placing the wordmark --------------------------------------------------
//
// Horizontally the wordmark centres on the RANGE. That is the same point as the
// mark's centre only because fitRange re-centres the range first — the maths still
// reads it off the fitted polygon so the two cannot drift apart.
//
// Vertically the wordmark hangs BELOW the bars: the top of its lowercase bodies sits
// on the bottom of the barcode, so only the ascenders (p, f, t) reach up into the
// mountains. The numbers are measured from the app's real font, Inter 800 — the
// wordmark inherits the page's font stack, so these are Inter's metrics, not the
// system fallback's. Re-measure all three if --font-sans changes.

/**
 * Wordmark font size, as a fraction of the mark's height.
 *
 * Growing this scales the word left, right and DOWN only -- the x-height line stays
 * pinned to the bar bottom whatever the size, because the lift below is itself a
 * multiple of the size. (The x-height line is the top of the 'p': it has no
 * ascender.) So this is safe to tune without re-deriving anything else.
 *
 * 0.60 fills 87% of the range's white space, up from the 0.45 it launched at (66%).
 * The ceiling is 0.62 and it is not the word's WIDTH that sets it -- the white space
 * narrows towards the mountains' feet, so what runs out first is vertical clearance
 * between the ascenders (p, f, t) and the ridge above them. Measured against the
 * real ridge at the word's own left and right edges: 0.62 leaves 9px of 200, 0.64
 * already punches through into the bars. 0.60 is chosen over 0.62 because the
 * clearance is proportional -- at the h=24 public-page sites 0.62 leaves about a
 * pixel, which Math.round on the font size and margin can eat.
 */
const CODE128_WORDMARK_SIZE = 0.60

/**
 * Where the baseline sits inside the wordmark's own line box, as a fraction of the
 * font size. line-height is 1, so the font's 0.97 ascent + 0.24 descent overflows
 * the box and the half-leading is negative: 1 - (0.97 + 0.24) all over 2, plus the
 * ascent, lands the baseline at 0.865.
 */
const CODE128_WORDMARK_BASELINE = 0.865

/** Inter 800's x-height, as a fraction of the font size. */
const CODE128_WORDMARK_X_HEIGHT = 0.55

export interface LogoProps {
  /** Rendered height of the BAR BAND in px. Everything else scales from this. */
  height?: number
  className?: string
  title?: string
  /**
   * What the barcode encodes. Defaults to "puresoft".
   *
   * This is an easter egg, not a way to relabel the logo: the visible wordmark
   * always reads "puresoft" whatever is encoded here. A longer payload makes a
   * wider, denser symbol — at a fixed height that means narrower modules.
   */
  payload?: string
}

export function Logo({ height, className, title = 'Puresoft', payload = CODE128_PAYLOAD }: LogoProps) {
  const uid = useId().replace(/[:]/g, '')
  const maskId = `ps-logo-${uid}`
  const h = height ?? 40
  const cls = className ? `ps-logo ${className}` : 'ps-logo'

  // Encoding is pure and cheap, but it would otherwise run on every render of every
  // logo on the page, and the payload almost never changes.
  const symbol = useMemo(() => encodeCode128B(payload), [payload])

  const w = symbol.totalModules
  // The symbol proper, excluding the quiet zones — the ramp spans this, not the
  // full viewBox, so the colour run lines up with the ink.
  const inkStart = QUIET_ZONE
  const inkWidth = w - QUIET_ZONE * 2
  const fitted = fitRange(BANNER_MOUNTAIN, w)

  const mark = (
    <svg viewBox={`0 0 ${w} ${CODE128_HEIGHT}`} height={h} role="img" aria-label={title} style={{ display: 'block' }}>
      <defs>
        <mask id={maskId}>
          <rect x="0" y="0" width={w} height={CODE128_HEIGHT} fill="white" />
          <polygon points={fitted} fill="black" />
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        {symbol.bars.map((bar, i) => {
          const centre = (bar.x + bar.width / 2 - inkStart) / inkWidth
          const stop = Math.min(CODE128_RAMP_STOPS, Math.max(1, Math.floor(centre * CODE128_RAMP_STOPS) + 1))
          return (
            <rect
              key={i}
              x={bar.x}
              y={0}
              width={bar.width}
              height={CODE128_HEIGHT}
              fill={`var(--ps-c${stop})`}
              // Bar edges must stay crisp: a half-pixel of antialiasing on a
              // one-module bar is a decoding error, not a cosmetic one.
              shapeRendering="crispEdges"
            />
          )
        })}
      </g>
      {BANNER_SNOW.map((points, i) => (
        <polygon key={i} points={fitRange(points, w)} fill="var(--ps-snow)" />
      ))}
    </svg>
  )

  // Centre the wordmark on the range, and hang it so the top of its lowercase
  // bodies lands on the bottom of the barcode. The line box follows the mark in the
  // column, so it starts at h and the margin walks it back up to where the x-height
  // line coincides with the bars' foot.
  const rangeXs = fitted.split(' ').map((point) => Number(point.split(',')[0]))
  const rangeCentre = (Math.min(...rangeXs) + Math.max(...rangeXs)) / 2

  const wordmarkSize = Math.round(h * CODE128_WORDMARK_SIZE)
  const wordmarkLift = wordmarkSize * (CODE128_WORDMARK_X_HEIGHT - CODE128_WORDMARK_BASELINE)

  return (
    <span className={cls} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      {mark}
      {/* The mark's <svg> already carries the accessible name, so the wordmark is
          decorative to a screen reader rather than a second copy of "Puresoft". */}
      <span
        aria-hidden="true"
        style={{
          fontWeight: 800,
          letterSpacing: '-0.02em',
          lineHeight: 1,
          fontSize: wordmarkSize,
          whiteSpace: 'nowrap',
          marginTop: Math.round(wordmarkLift),
          // align-items:center centres the MARGIN box, so a margin of M moves the
          // text by M/2 — hence the doubling.
          marginLeft: Math.round((rangeCentre - w / 2) * (h / CODE128_HEIGHT) * 2),
        }}
      >
        <span style={{ color: 'var(--ps-pure)' }}>pure</span>
        <span style={{ color: 'var(--ps-soft)' }}>soft</span>
      </span>
    </span>
  )
}

export default Logo
